import React, { useState, useRef } from "react";
import "./CustomEditor.css"; // Import your CSS file for styling
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBold, faItalic, faUnderline, faStrikethrough, faLink, faImage, faListOl, faListUl, faIndent, faQuoteRight, faHeading, faEraser, } from "@fortawesome/free-solid-svg-icons";

const CustomEditor = () => {
  const [content, setContent] = useState("");
  const [fontSize, setFontSize] = useState(14);
  const [fontFamily, setFontFamily] = useState("Arial");
  const [theme, setTheme] = useState("light");
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [strikeThrough, setStrikeThrough] = useState(false);
  const [alignment, setAlignment] = useState("left");
  const [listType, setListType] = useState("none");
  const textAreaRef = useRef();

  const handleInputChange = (event) => {
    setContent(event.target.value);
    console.log(event.target.value)
  };

  const handleFontSizeChange = (event) => {
    setFontSize(parseInt(event.target.value, 10));
  };

  const handleFontFamilyChange = (event) => {
    setFontFamily(event.target.value);
  };

  const handleThemeChange = (event) => {
    setTheme(event.target.value);
  };

  const toggleBold = () => {
    setBold(!bold);
  };

  const toggleItalic = () => {
    setItalic(!italic);
  };

  const toggleUnderline = () => {
    setUnderline(!underline);
  };

  const toggleStrikeThrough = () => {
    setStrikeThrough(!strikeThrough);
  };

  const handleAlignmentChange = (alignmentValue) => {
    setAlignment(alignmentValue);
  };

  const handleListChange = (type) => {
    switch (type) {
      case "orderedList":
        document.execCommand("insertOrderedList");
        setListType("orderedList");
        break;
      case "unorderedList":
        document.execCommand("insertUnorderedList");
        setListType("unorderedList");
        break;
      default:
        break;
    }
  };

  const handleTextColorChange = (color) => {
    document.execCommand("foreColor", false, color);
  };

  const handleTextIndent = () => {
    document.execCommand("indent");
  };

  const handleBlockquote = () => {
    document.execCommand("formatBlock", false, "blockquote");
  };

  const handleHeadingStyle = (headingTag) => {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const selectedNode = range.commonAncestorContainer;
  
    if (selectedNode.nodeType === Node.TEXT_NODE) {
      const newElement = document.createElement(headingTag);
      newElement.appendChild(document.createTextNode(selectedNode.textContent));
      range.deleteContents();
      range.insertNode(newElement);
    } else {
      const allSelectedNodes = selection.getRangeAt(0).cloneContents().childNodes;
      const newElement = document.createElement(headingTag);
      allSelectedNodes.forEach((node) => newElement.appendChild(node.cloneNode(true)));
      range.deleteContents();
      range.insertNode(newElement);
    }
  };

  const handleClearFormatting = () => {
    document.execCommand("removeFormat");
  };

  const handleSpellCheck = () => {
    document.execCommand("defaultParagraphSeparator", false, "p");
    document.execCommand("enableObjectResizing", false, "false");
    document.execCommand("enableInlineTableEditing", false, "false");
    document.execCommand("styleWithCSS", false, "true");
    document.execCommand("useCSS", false, "true");
  };

  const handleInsertLink = () => {
    const url = prompt("Enter the URL:");
    document.execCommand("insertHTML", false, `<a href="${url}">Link</a>`);
  };

  const handleInsertImage = () => {
    const imageUrl = prompt("Enter the image URL:");
    document.execCommand("insertImage", false, imageUrl);
  };

  const applyStyles = () => {
    let style = {
      fontSize: `${fontSize}px`,
      fontFamily,
      fontWeight: bold ? "bold" : "normal",
      fontStyle: italic ? "italic" : "normal",
      textDecoration: underline
        ? "underline"
        : strikeThrough
        ? "line-through"
        : "none",
      textAlign: alignment,
    };

    return style;
  };

  return (
    <div className={`editor-container ${theme === "dark" ? "dark-theme" : ""}`}>
      <h2>Custom Text Editor</h2>
      <div className="editor-controls">
        <label htmlFor="fontSize">Font Size:</label>
        <input
          type="number"
          id="fontSize"
          value={fontSize}
          onChange={handleFontSizeChange}
        />
        <label htmlFor="fontFamily">Font Family:</label>
        <select
          id="fontFamily"
          value={fontFamily}
          onChange={handleFontFamilyChange}
        >
          <option value="Arial">Arial</option>
          <option value="Verdana">Verdana</option>
          <option value="Helvetica">Helvetica</option>
        </select>
        <label htmlFor="theme">Theme:</label>
        <select id="theme" value={theme} onChange={handleThemeChange}>
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </select>
        <button onClick={toggleBold}>
            <FontAwesomeIcon
              icon={faBold}
              className={bold ? "active" : ""}
            />
          </button>
          <button onClick={toggleItalic}>
            <FontAwesomeIcon
              icon={faItalic}
              className={italic ? "active" : ""}
            />
          </button>
          <button onClick={toggleUnderline}>
            <FontAwesomeIcon
              icon={faUnderline}
              className={underline ? "active" : ""}
            />
          </button>
          <button onClick={toggleStrikeThrough}>
            <FontAwesomeIcon
              icon={faStrikethrough}
              className={strikeThrough ? "active" : ""}
            />
          </button>
        <select
          value={alignment}
          onChange={(e) => handleAlignmentChange(e.target.value)}
        >
          <option value="left">Left Align</option>
          <option value="center">Center Align</option>
          <option value="right">Right Align</option>
        </select>
        <button onClick={() => handleListChange("orderedList")}>
          <FontAwesomeIcon icon={faListOl} />
        </button>
        <button onClick={() => handleListChange("unorderedList")}>
          <FontAwesomeIcon icon={faListUl} />
        </button>
        <input
          type="color"
          onChange={(e) => handleTextColorChange(e.target.value)}
        />
        <button onClick={handleSpellCheck}>
          <FontAwesomeIcon icon={faEraser} />
        </button>
        <button onClick={handleInsertLink}>
          <FontAwesomeIcon icon={faLink} />
        </button>
        <button onClick={handleInsertImage}>
          <FontAwesomeIcon icon={faImage} />
        </button>
        <button onClick={handleTextIndent}>
          <FontAwesomeIcon icon={faIndent} />
        </button>
        <button onClick={handleBlockquote}>
          <FontAwesomeIcon icon={faQuoteRight} />
        </button>
        <button onClick={() => handleHeadingStyle("h1")}>
          <FontAwesomeIcon icon={faHeading} />
        </button>
        <button onClick={handleClearFormatting}>
          <FontAwesomeIcon icon={faEraser} />
        </button>
      </div>

      <div>
      <div
          ref={textAreaRef}
          contentEditable="true"
          className={`textarea ${listType !== "none" ? listType : ""}`}
          style={applyStyles()}
          onInput={handleInputChange}
        >
          {content}
        </div>
      </div>

    </div>
  );
};

export default CustomEditor;
